import { siteConfig } from '../data/siteConfig';

/**
 * Meta Pixel (browser) for InstaMovers Florida's dataset.
 * Only standard events with non-personal parameters — never names, emails, phones, addresses, notes or inventory.
 */
type Fbq = ((...args: unknown[]) => void) & { callMethod?: (...a: unknown[]) => void; queue: unknown[][]; push: Fbq; loaded: boolean; version: string };

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

let initialized = false;
let pageViewFired = false;

function pixelId(): string | null {
  const id = siteConfig.metaPixelId?.trim();
  return id ? id : null;
}

/** Loads fbevents.js and calls init once (standard Meta base code). */
export function initPixel(): boolean {
  const id = pixelId();
  if (!id || typeof window === 'undefined') return false;
  if (initialized) return true;
  if (!window.fbq) {
    const n = function (...args: unknown[]) {
      if (n.callMethod) n.callMethod(...args);
      else n.queue.push(args);
    } as Fbq;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    window.fbq = n;
    if (!window._fbq) window._fbq = n;
    const s = document.createElement('script');
    s.async = true;
    s.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const first = document.getElementsByTagName('script')[0];
    if (first?.parentNode) first.parentNode.insertBefore(s, first);
    else document.head.appendChild(s);
  }
  window.fbq('init', id);
  initialized = true;
  return true;
}

/** PageView once per page load (the funnel is a single page; step changes are not new page loads). */
export function firePageView(): void {
  if (pageViewFired || !initPixel()) return;
  pageViewFired = true;
  window.fbq?.('track', 'PageView');
}

type Params = Record<string, string | number | string[]>;

/** Stable move identifiers used as content_ids. */
export function moveContentId(bedrooms: string | null): string {
  switch (bedrooms) {
    case 'studio':
      return 'move_studio';
    case '1':
    case '2':
    case '3':
    case '4':
      return `move_${bedrooms}br`;
    case '5':
      return 'move_5plus';
    case 'few':
      return 'move_few_items';
    default:
      return 'move_unspecified';
  }
}

const viewed = new Set<string>();

/** ViewContent when a move size is selected. Once per size per page load. */
export function fireViewContent(contentId: string, params: Params = {}): void {
  if (viewed.has(contentId) || !initPixel()) return;
  viewed.add(contentId);
  window.fbq?.('track', 'ViewContent', { content_ids: [contentId], content_type: 'product', ...params });
}

/** Lead only after the backend persisted the request. eventId must match the server CAPI event_id. */
export function fireLead(eventId: string, params: Params = {}): void {
  if (!initPixel()) return;
  window.fbq?.('track', 'Lead', { content_type: 'product', ...params }, { eventID: eventId });
}

/** AddToCart when the customer clicks the button that sends them to Shopify. Value = the deposit amount in the cart, not the move estimate. */
export function fireAddToCart(value: number, eventId: string, params: Params = {}): void {
  if (!initPixel()) return;
  window.fbq?.('track', 'AddToCart', { value, currency: 'USD', content_type: 'product', content_name: 'Moving deposit', ...params }, { eventID: eventId });
}

/** InitiateCheckout when a real checkout starts. Value = deposit being paid now. Estimated move value goes in its own parameter. */
export function fireInitiateCheckout(value: number, eventId: string, params: Params = {}): void {
  if (!initPixel()) return;
  window.fbq?.('track', 'InitiateCheckout', { value, currency: 'USD', content_type: 'product', num_items: 1, ...params }, { eventID: eventId });
}

/**
 * Purchase is intentionally NOT fired from the browser. It must come from the server after a verified
 * payment webhook (Stripe/Shopify), with value = amount actually collected and the same event_id the
 * browser would use, so Meta deduplicates.
 */

/** Meta browser identifiers, sent to our backend (not to the URL) for CAPI matching where consent allows. */
export function metaCookies(): { fbp?: string; fbc?: string } {
  if (typeof document === 'undefined') return {};
  const get = (n: string) => document.cookie.split('; ').find((c) => c.startsWith(`${n}=`))?.split('=')[1];
  return { fbp: get('_fbp'), fbc: get('_fbc') };
}
